import { useEffect } from "react";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { retainedQueryData } from "@sx/api-client";
import { Page, Skeleton } from "@sx/ui";

import { usePrincipalId } from "../../../lib/browser-state";
import { registerWorkspaceHeader } from "../../../lib/nav";
import { started } from "../../../lib/prefetch";
import { loadedSession } from "../../../lib/session";
import { WorkspaceGate } from "../../../shell/WorkspaceGate";
import { api } from "../../../workspaces/production/api";
import { floorGrantsQuery } from "../../../workspaces/production/grants-query";
import { CAPTURE_NAV, captureNavFor } from "../../../workspaces/production/nav";
import { SeatProvider } from "../../../workspaces/production/seat";
import { FLOOR_ROLE_LABELS } from "../../../workspaces/production/vocabulary";

export const Route = createFileRoute("/_console/production")({
  // Started, not awaited, beside every other loader: the grant is asked on click and the
  // layout below draws Production's frame while it is on its way.
  loader: async ({ context }) => {
    const { api: loaderApi } = await import("../../../workspaces/production/api");
    const principalId = loadedSession(context.queryClient)?.principal.id;
    if (principalId === undefined) return;
    void started(
      context.queryClient.ensureQueryData(
        floorGrantsQuery(principalId, loaderApi.grants),
      ),
    );
  },
  component: CaptureLayout,
});

/* Capture's one grant boundary.
 *
 * The session was established above this layout, so the question here is not
 * who you are but what the factory door grants you — and `WorkspaceGate` is the
 * console's one answer to every shape that question can take. Everything below
 * can assume a resolved operator, so nothing below mounts before the grant: while it is
 * on its way the layout draws Production's frame — its tabs are already in the bar — and
 * a missing grant is the gate's refusal. */
function CaptureLayout() {
  const grants = useQuery(floorGrantsQuery(usePrincipalId(), api.grants));

  // The role is the factory door's grant, not the platform identity, so it rides in the
  // account menu only while Capture is the workspace on screen.
  //
  // Read through `retainedQueryData`, never `grants.data`: react-query keeps the last
  // success when a refetch fails, so a grant restored from the last reload would outlive
  // its own revocation and the gate's 403 branch would be unreachable. A permanent
  // refusal drops the snapshot; a door being briefly down keeps it.
  const role = retainedQueryData(grants);
  useEffect(
    () =>
      registerWorkspaceHeader({
        workspace: "experience-production",
        items: role === undefined ? CAPTURE_NAV : captureNavFor(role),
        badges:
          role === undefined
            ? []
            : [{ label: FLOOR_ROLE_LABELS[role], variant: "secondary" }],
      }),
    [role],
  );

  if (role === undefined)
    return (
      <WorkspaceGate
        workspace="experience-production"
        query={grants}
        pending={
          <Page layout="page" role="status" aria-label="Loading Production">
            <Skeleton className="h-40 w-full" />
          </Page>
        }
      />
    );

  return (
    <SeatProvider seat={role}>
      <Outlet />
    </SeatProvider>
  );
}
